import { ColumnDef } from '@tanstack/react-table'
import { store } from 'store/index'
import { NameCell, PeopleCell, SkillGapCell, CoverageCell } from './cells'

export type OrgSkillGapsTableRow = {
  id: string
  variantId: string
  name: string
  imageUrl?: string | null
  description: string | null
  first3UserIds: string[]
  teamIds: string[]
  userCount: number
  averageRequiredLevel: number
  averageAssessedLevel: number
  averageAssessedRequiredLevel: number
  assessedSkillGap: number
  checkinCoverage: number
  checkinCount: number
  donutChartData?: {
    segments: Array<{
      labelSlug: string
      labelName: string
      color: string
      count: number
    }>
    notCheckedIn: number
    total: number
  }
  individualSkillGaps?: Array<{
    name: string
    requiredLevel: string
    assessedLevel: string
    skillGap: string
  }>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HeaderContent = any
type HeaderItem = { key: string; value: string }

const findHeader = (key: string, headerContent: HeaderItem[]) =>
  headerContent.find((item: HeaderItem) => item.key === key)?.value

export class Data {
  getColumnsWithHeaderContent(
    headerContent: HeaderContent,
    onDrawerClick?: (
      columnKey: string,
      columnTitle: string,
      columnInfo: string,
      userCount: number,
      rowData?: OrgSkillGapsTableRow
    ) => void
  ) {
    return [
      {
        accessorKey: 'name',
        cell: NameCell,
        enableGlobalFilter: true,
        header: findHeader('name', headerContent),
        id: 'name',
      },
      {
        accessorKey: 'userCount',
        enableGlobalFilter: false,
        id: 'users',
        cell: PeopleCell,
        header: findHeader('user_count', headerContent),
        sortingFn: 'alphanumeric',
        meta: {
          onDrawerClick: onDrawerClick
            ? (row: OrgSkillGapsTableRow) =>
                onDrawerClick(
                  'user_count',
                  row.name,
                  'The number of users in positions that require this skill',
                  row.userCount,
                  row
                )
            : undefined,
        },
      },
      {
        accessorKey: 'checkinCoverage',
        enableGlobalFilter: false,
        id: 'checkinCoverage',
        cell: CoverageCell,
        header: findHeader('checkin_coverage', headerContent),
        sortingFn: 'alphanumeric',
      },
      {
        accessorKey: 'averageAssessedRequiredLevel',
        enableGlobalFilter: false,
        id: 'averageAssessedRequiredLevel',
        header: findHeader('average_assessed_required_level', headerContent),
        sortingFn: 'alphanumeric',
      },
      {
        accessorKey: 'averageAssessedLevel',
        enableGlobalFilter: false,
        id: 'averageAssessedLevel',
        header: findHeader('average_assessed_level', headerContent),
        sortingFn: 'alphanumeric',
      },
      {
        accessorKey: 'assessedSkillGap',
        cell: SkillGapCell,
        enableGlobalFilter: false,
        id: 'assessedSkillGap',
        header: findHeader('assessed_skill_gap', headerContent),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        sortingFn: (_rowA: any, _rowB: any) => {
          return (
            _rowA.original.assessedSkillGap - _rowB.original.assessedSkillGap
          )
        },
      },
    ] satisfies ColumnDef<OrgSkillGapsTableRow, never>[]
  }

  get variantsEnabled() {
    return store.featureGates.canAccess('skill_variants')
  }
}
